import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { Room, RoomEvent, DataPacket_Kind, ConnectionState } from 'livekit-client';
import environment from '../config/environment';
import './AstrologyChatbot.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const MAX_RECONNECT_ATTEMPTS = 5;
const MESSAGE_TIMEOUT = 30000;
const HEARTBEAT_INTERVAL = 10000;
const BOT_START_TIMEOUT = 15000;
const DEV_MODE = false;
const MSG_BUFFER_KEY = 'astro_msg_buffer';
const PENDING_QUEUE_KEY = 'astro_pending_queue';

const devError = (...a) => { if (DEV_MODE) console.error(...a); };
const delay = (ms) => new Promise(r => setTimeout(r, ms));

export default function AstrologyChatbot() {

  // ── State ───────────────────────────────────────────────────────────────────
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [astrologerName, setAstrologerName] = useState('రామస్వామి శర్మ');
  const [astrologerImage, setAstrologerImage] = useState(
    'https://d1aah1uz69878a.cloudfront.net/poojari/onboarding/2350/profile/1765179098847-217035.png'
  );
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState(null);

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isChatDisabled, setIsChatDisabled] = useState(false);
  const [isLivekitConnected, setIsLivekitConnected] = useState(false);
  const [showRetryPrompt, setShowRetryPrompt] = useState(false);
  const [showContinueChatPrompt, setShowContinueChatPrompt] = useState(false);
  const [pujaBookingUrl, setPujaBookingUrl] = useState('');

  const [timerDisplay, setTimerDisplay] = useState('05:00');
  const [timerSeconds, setTimerSeconds] = useState(300);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isTimerExpired, setIsTimerExpired] = useState(false);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef(null);


  const livekitRoomRef = useRef(null);
  const botStatusRef = useRef('idle');
  const isConnectingRef = useRef(false);
  const isBotStartingRef = useRef(false);
  const connectionAttemptRef = useRef(0);
  const isRecoveringSilentlyRef = useRef(false);
  const pendingMessagesQueueRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageTimeoutRef = useRef(null);
  const botStartTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const timerSecondsRef = useRef(300);
  const isChatDisabledRef = useRef(false);
  const showContinueChatPromptRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => { isChatDisabledRef.current = isChatDisabled; }, [isChatDisabled]);
  useEffect(() => { showContinueChatPromptRef.current = showContinueChatPrompt; }, [showContinueChatPrompt]);
  useEffect(() => { timerSecondsRef.current = timerSeconds; }, [timerSeconds]);

  // ── Scroll to bottom ────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  // ── Bootstrap on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    // Try to get mobile from URL params (WebView) or cookies, then fallback to localStorage
    const getMobileFromSource = () => {
      // 1. URL query param: ?mobile=9876543210
      const urlParams = new URLSearchParams(window.location.search);
      const paramMobile = urlParams.get('mobile');
      if (paramMobile) return paramMobile;

      // 2. Cookie: mobile=9876543210
      const cookieMatch = document.cookie.match(/(?:^|;\s*)mobile=([^;]*)/);
      if (cookieMatch) return decodeURIComponent(cookieMatch[1]);

      // 3. localStorage fallback
      return localStorage.getItem('mobile');
    };

    const mobile = getMobileFromSource();
    if (mobile) {
      localStorage.setItem('mobile', mobile);
      localStorage.setItem('token', 'webview_session');
      const details = JSON.parse(localStorage.getItem('astrologer_details') || '{}');
      if (details.astrologer_name) setAstrologerName(details.astrologer_name);
      if (details.astrologer_img) setAstrologerImage(details.astrologer_img);
      initializeSession();
    }
    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      if (botStartTimeoutRef.current) clearTimeout(botStartTimeoutRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (livekitRoomRef.current) { try { livekitRoomRef.current.disconnect(); } catch {} }
    };
  }, []);

  // ── localStorage helpers ────────────────────────────────────────────────────
  const bufferMessages = (msgs) => {
    try { localStorage.setItem(MSG_BUFFER_KEY, JSON.stringify(msgs)); } catch {}
  };
  const loadBufferedMessages = () => {
    try {
      const r = localStorage.getItem(MSG_BUFFER_KEY);
      return r ? JSON.parse(r).map(m => ({ ...m, timestamp: new Date(m.timestamp) })) : [];
    } catch { return []; }
  };
  const bufferPendingQueue = (q) => {
    try { localStorage.setItem(PENDING_QUEUE_KEY, JSON.stringify(q)); } catch {}
  };
  const clearRuntimeBuffers = () => {
    localStorage.removeItem(MSG_BUFFER_KEY);
    localStorage.removeItem(PENDING_QUEUE_KEY);
  };

  // ── Timer ───────────────────────────────────────────────────────────────────
  const updateTimerDisplay = useCallback((secs) => {
    const m = Math.floor(secs / 60), s = secs % 60;
    setTimerDisplay(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
  }, []);

  const stopTimer = useCallback(() => {
    setIsTimerRunning(false);
    if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
  }, []);

  const startTimer = useCallback((seconds) => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    const s = seconds ?? 300;
    setTimerSeconds(s); timerSecondsRef.current = s;
    updateTimerDisplay(s); setIsTimerExpired(false); setIsTimerRunning(true);
    timerIntervalRef.current = setInterval(() => {
      timerSecondsRef.current -= 1;
      setTimerSeconds(timerSecondsRef.current);
      updateTimerDisplay(timerSecondsRef.current);
      if (timerSecondsRef.current <= 0) {
        setIsTimerExpired(true); setIsTimerRunning(false);
        clearInterval(timerIntervalRef.current); timerIntervalRef.current = null;
      }
    }, 1000);
  }, [updateTimerDisplay]);

  const syncTimerFromServer = useCallback((timerData) => {
    if (!timerData) return;
    if (timerData.is_expired) {
      setIsTimerExpired(true); setIsTimerRunning(false);
      setTimerSeconds(0); setTimerDisplay('00:00');
      if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
      return;
    }
    if (timerData.remaining_seconds !== undefined) {
      setTimerSeconds(timerData.remaining_seconds);
      updateTimerDisplay(timerData.remaining_seconds);
      if (!timerIntervalRef.current) startTimer(timerData.remaining_seconds);
    }
  }, [startTimer, updateTimerDisplay]);

  // ── Messages ────────────────────────────────────────────────────────────────
  const addMessage = useCallback((role, content, imgUrl, isImg) => {
    const msg = { role, content, timestamp: new Date(), img_url: imgUrl, is_img: isImg };
    setMessages(prev => {
      const next = [...prev, msg];
      bufferMessages(next);
      return next;
    });
  }, []);

  // ── LiveKit cleanup ─────────────────────────────────────────────────────────
  const cleanupConnection = useCallback(async () => {
    if (reconnectTimeoutRef.current) { clearTimeout(reconnectTimeoutRef.current); reconnectTimeoutRef.current = null; }
    if (messageTimeoutRef.current) { clearTimeout(messageTimeoutRef.current); messageTimeoutRef.current = null; }
    if (botStartTimeoutRef.current) { clearTimeout(botStartTimeoutRef.current); botStartTimeoutRef.current = null; }
    if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
    if (livekitRoomRef.current) {
      try { await livekitRoomRef.current.disconnect(); } catch {}
      livekitRoomRef.current = null;
    }
    setIsLivekitConnected(false);
    isConnectingRef.current = false;
    setIsSending(false);
  }, []);

  // ── LiveKit token ───────────────────────────────────────────────────────────
  const fetchLivekitToken = useCallback(async (retryCount = 0) => {
    const custMobile = localStorage.getItem('mobile');
    if (!custMobile) throw new Error('Mobile not found');
    try {
      const res = await fetch(`${environment.LIVEKIT_URL}/api/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room: custMobile, username: custMobile }),
      });
      const data = await res.json();
      if (!data.token) throw new Error('Invalid token');
      return data.token;
    } catch (err) {
      if (retryCount < 2) { await delay((retryCount + 1) * 1000); return fetchLivekitToken(retryCount + 1); }
      throw err;
    }
  }, []);

  // ── LiveKit incoming message handler ────────────────────────────────────────
  const handleLivekitMessage = useCallback((payload) => {
    try {
      if (messageTimeoutRef.current) { clearTimeout(messageTimeoutRef.current); messageTimeoutRef.current = null; }
      const data = JSON.parse(new TextDecoder().decode(payload));
      if (data.type === 'ping') return;

      console.log(data,"------------dataaa")

      // System message — check if chat should be disabled
      if (data.role === 'system' && data.content) {
        if (data.can_chat === false || data?.timer?.is_expired) {
          setIsChatDisabled(true);
          return;
        }
        addMessage('system', data.content, data.img_url, data.is_img);
        setIsSending(false);
        return;
      }

      // Assistant message
      if (data.role === 'assistant' && data.content) {
        if (data.can_chat === false || data?.timer?.is_expired) setIsChatDisabled(true);
        const pujaMatch = data.content.match(/https?:\/\/vedamandir\.com\/pooja[^\s]*/);
        if (pujaMatch) setPujaBookingUrl(pujaMatch[0]);
        if (data.timer) syncTimerFromServer(data.timer);
        addMessage('assistant', data.content, data.img_url, data.is_img);
        setIsSending(false);
        return;
      }

      if (data.type === 'error') {
        setIsSending(false);
      }
    } catch { setIsSending(false); }
  }, [addMessage, syncTimerFromServer]);

  // ── Connection lost handler ─────────────────────────────────────────────────
  const handleConnectionLost = useCallback(() => {
    if (isRecoveringSilentlyRef.current) return;
    setIsLivekitConnected(false);
    if (!isChatDisabledRef.current) silentRecover();
  }, []);

  // ── Heartbeat ───────────────────────────────────────────────────────────────
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    heartbeatIntervalRef.current = setInterval(() => {
      if (livekitRoomRef.current?.state === ConnectionState.Disconnected) handleConnectionLost();
    }, HEARTBEAT_INTERVAL);
  }, [handleConnectionLost]);

  // ── Connect to LiveKit ──────────────────────────────────────────────────────
  const connectToLivekit = useCallback(async () => {
    if (isConnectingRef.current) return;
    if (livekitRoomRef.current?.state === ConnectionState.Connected) { setIsLivekitConnected(true); return; }
    isConnectingRef.current = true;
    connectionAttemptRef.current += 1;
    try {
      await cleanupConnection();
      const token = await fetchLivekitToken();
      const room = new Room({
        adaptiveStream: true, dynacast: true, disconnectOnPageLeave: true,
        reconnectPolicy: { nextRetryDelayInMs: (ctx) => Math.min(1000 * Math.pow(2, ctx.retryCount), 10000) },
        rtcConfig: {
          iceTransportPolicy: 'all',
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
          ],
        },
      });
      livekitRoomRef.current = room;

      room.on(RoomEvent.Connected, () => {
        setIsLivekitConnected(true);
        connectionAttemptRef.current = 0;
        setShowRetryPrompt(false);
        startHeartbeat();
      });
      room.on(RoomEvent.Disconnected, (reason) => {
        setIsLivekitConnected(false);
        if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
        if (reason !== 'user initiated') handleConnectionLost();
      });
      room.on(RoomEvent.Reconnecting, () => setIsLivekitConnected(false));
      room.on(RoomEvent.Reconnected, () => {
        setIsLivekitConnected(true);
        setShowRetryPrompt(false);
        if (!isChatDisabledRef.current) silentRecover();
      });
      room.on(RoomEvent.DataReceived, (payload) => handleLivekitMessage(payload));
      room.on(RoomEvent.ConnectionStateChanged, (state) =>
        setIsLivekitConnected(state === ConnectionState.Connected)
      );

      await room.connect(environment.LIVEKIT_WSS_URL, token);
    } catch {
      setIsLivekitConnected(false);
      isConnectingRef.current = false;
      if (connectionAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
        await delay(2000);
        await connectToLivekit();
      } else {
        setShowRetryPrompt(true);
      }
    } finally { isConnectingRef.current = false; }
  }, [cleanupConnection, fetchLivekitToken, handleLivekitMessage, startHeartbeat, handleConnectionLost]);

  // ── Send message via LiveKit data channel ───────────────────────────────────
  const sendMessageToRoom = useCallback(async (messageData) => {
    if (!livekitRoomRef.current) throw new Error('Room not connected');
    const wordCount = messageData.content.split(/\s+/).length;
    const d = environment.TYPING_DELAY_BASE + wordCount * environment.TYPING_DELAY_PER_WORD;
    setTimeout(() => setIsSending(true), d);
    const encoded = new TextEncoder().encode(JSON.stringify(messageData));
    await livekitRoomRef.current.localParticipant.publishData(encoded, DataPacket_Kind.RELIABLE);
  }, []);

  // ── Bot start / stop ────────────────────────────────────────────────────────
  const stopBot = useCallback(async () => {
    if (botStatusRef.current === 'stopping' || botStatusRef.current === 'stopped') return;
    botStatusRef.current = 'stopping';
    const custMobile = localStorage.getItem('mobile');
    if (!custMobile) { botStatusRef.current = 'stopped'; return; }
    try {
      await fetch('https://chatbot.vedamandir.com/api/bot/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_name: custMobile }),
      });
    } catch {} finally { botStatusRef.current = 'stopped'; }
  }, []);

  const startBot = useCallback(async () => {
    if (isBotStartingRef.current) return;
    isBotStartingRef.current = true;
    botStatusRef.current = 'starting';
    const custMobile = localStorage.getItem('mobile');
    if (!custMobile) {
      botStatusRef.current = 'error';
      isBotStartingRef.current = false;
      addMessage('assistant', 'క్షమించండి, పేజీని రిఫ్రెష్ చేయండి.');
      return;
    }
    try {
      const timeoutP = new Promise((_, rej) =>
        setTimeout(() => rej(new Error('Bot start timeout')), BOT_START_TIMEOUT)
      );
      const fetchP = fetch(`${environment.LIVEKIT_BOT_URL}/api/bot/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // No birth data — bot will collect details through the chat conversation
        body: JSON.stringify({ room_name: custMobile }),
      }).then(r => r.json());

      const response = await Promise.race([fetchP, timeoutP]);

      if (
        response?.status === 'success' ||
        response?.status === 'started' ||
        response?.status === 'already_running'
      ) {
        botStatusRef.current = 'running';
        isBotStartingRef.current = false;
        setIsSending(response.status !== 'already_running');
        startTimer(response.timer?.remaining_seconds ?? 300);
        messageTimeoutRef.current = setTimeout(() => {}, MESSAGE_TIMEOUT);
      } else {
        throw new Error(response?.detail || 'Invalid bot start response');
      }
    } catch (err) {
      isBotStartingRef.current = false;
      botStatusRef.current = 'error';
      devError('Bot start error:', err);
    }
  }, [addMessage, startTimer]);

  // ── Pending message processor ───────────────────────────────────────────────
  const processPendingMessages = useCallback(async () => {
    if (botStatusRef.current !== 'running' || pendingMessagesQueueRef.current.length === 0) return;
    const queue = [...pendingMessagesQueueRef.current];
    const failed = [];
    pendingMessagesQueueRef.current = [];
    bufferPendingQueue([]);
    for (const msg of queue) {
      try { await sendMessageToRoom(msg); await delay(200); }
      catch { failed.push(msg); }
    }
    pendingMessagesQueueRef.current = failed;
    bufferPendingQueue(failed);
  }, [sendMessageToRoom]);

  // ── Silent recovery ─────────────────────────────────────────────────────────
  const silentRecover = useCallback(async () => {
    if (
      isRecoveringSilentlyRef.current ||
      isChatDisabledRef.current ||
      showContinueChatPromptRef.current
    ) return;
    isRecoveringSilentlyRef.current = true;
    try {
      if (!livekitRoomRef.current || livekitRoomRef.current.state !== ConnectionState.Connected) {
        await connectToLivekit();
      }
      let attempts = 0;
      while (livekitRoomRef.current?.state !== ConnectionState.Connected && attempts < 5) {
        await delay(500); attempts++;
      }
      if (botStatusRef.current !== 'running' && botStatusRef.current !== 'error') {
        await startBot();
      }
      await processPendingMessages();
    } finally { isRecoveringSilentlyRef.current = false; }
  }, [connectToLivekit, startBot, processPendingMessages]);

  // ── Session check ───────────────────────────────────────────────────────────
  const checkSessionStatus = useCallback(async () => {
    const mobile = localStorage.getItem('mobile');
    if (!mobile) return null;
    try {
      const res = await fetch(
        `${environment.LIVEKIT_BOT_URL}/api/astrology/session-status?mobile=${mobile}`
      );
      const response = await res.json();
      const data = response.data || response;
      if (data.messages) {
        data.messages = data.messages.map(m => ({ ...m, timestamp: new Date(m.timestamp) }));
      }
      if (data.assigned_astrologer_name && data.assigned_astrologer_image) {
        localStorage.setItem('astrologer_details', JSON.stringify({
          astrologer_name: data.assigned_astrologer_name,
          astrologer_img: data.assigned_astrologer_image,
        }));
        setAstrologerName(data.assigned_astrologer_name);
        setAstrologerImage(data.assigned_astrologer_image);
      }
      return data;
    } catch { return null; }
  }, []);

  // ── Connect + boot bot (no form data needed) ────────────────────────────────
  const connectAndStart = useCallback(async () => {
    await connectToLivekit();
    let waitCount = 0;
    const check = setInterval(async () => {
      if (livekitRoomRef.current?.state === ConnectionState.Connected || waitCount > 20) {
        clearInterval(check);
        if (livekitRoomRef.current?.state === ConnectionState.Connected) {
          await startBot();
        } else {
          setShowRetryPrompt(true);
          addMessage('assistant', 'క్షమించండి, కనెక్షన్ సమస్య ఉంది. దయచేసి మళ్ళీ ప్రయత్నించండి.');
        }
      }
      waitCount++;
    }, 500);
  }, [connectToLivekit, startBot, addMessage]);

  // ── Initialize session ──────────────────────────────────────────────────────
  const initializeSession = useCallback(async () => {
    setIsLoadingSession(true);
    try {
      const session = await checkSessionStatus();

      if (!session) {
        // No server session — load any local cache then connect fresh
        const buffered = loadBufferedMessages();
        if (buffered.length > 0) setMessages(buffered);
        await connectAndStart();
        return;
      }

      if (!session.has_active_session) {
        // Previous session ended — restore history, offer to continue
        clearRuntimeBuffers();
        const msgs = session.messages || [];
        setMessages(msgs);
        if (msgs.length > 0) {
          setShowContinueChatPrompt(true);
          setIsChatDisabled(false);
        } else {
          await connectAndStart();
        }
        return;
      }

      // Active session — restore everything and offer continue button
      const msgs = session.messages || [];
      setMessages(msgs);
      pendingMessagesQueueRef.current = [];
      clearRuntimeBuffers();
      bufferMessages(msgs);

      if (session.can_chat) {
        setIsChatDisabled(false);
        setShowContinueChatPrompt(true);
        if (session.remaining_seconds !== undefined) {
          setTimerSeconds(session.remaining_seconds);
          updateTimerDisplay(session.remaining_seconds);
        }
      } else {
        setIsChatDisabled(true);
        setShowContinueChatPrompt(false);
      }
    } catch {
      await connectAndStart();
    } finally {
      setIsLoadingSession(false);
    }
  }, [checkSessionStatus, connectAndStart, updateTimerDisplay]);

  // ── Chat controls ───────────────────────────────────────────────────────────
  const continueChat = async () => {
    setShowContinueChatPrompt(false);
    showContinueChatPromptRef.current = false;
    botStatusRef.current = 'idle';
    await silentRecover();
  };

  const endChatSession = async () => {
    setShowContinueChatPrompt(true);
    stopTimer();
    await cleanupConnection();
    try { await stopBot(); } catch {}
  };

  const closeChatModule = () => {
    stopTimer();
    cleanupConnection();
    clearRuntimeBuffers();
    window.location.href = '/';
  };

  // ── Send message ────────────────────────────────────────────────────────────
  const onSendMessage = async (e) => {
    e?.preventDefault();
    if (!inputMessage.trim() || isChatDisabled) return;
    const userMessage = inputMessage.trim();
    setInputMessage('');
    addMessage('user', userMessage);
    const queuedMsg = {
      role: 'user',
      content: userMessage,
      room_name: localStorage.getItem('mobile'),
    };
    pendingMessagesQueueRef.current.push(queuedMsg);
    bufferPendingQueue(pendingMessagesQueueRef.current);

    if (
      livekitRoomRef.current?.state === ConnectionState.Connected &&
      botStatusRef.current === 'running'
    ) {
      try {
        await sendMessageToRoom(queuedMsg);
        const idx = pendingMessagesQueueRef.current.indexOf(queuedMsg);
        if (idx >= 0) pendingMessagesQueueRef.current.splice(idx, 1);
        bufferPendingQueue(pendingMessagesQueueRef.current);
        return;
      } catch {}
    }
    silentRecover();
  };

  // ── Manual reconnect ────────────────────────────────────────────────────────
  const manualReconnect = async () => {
    setShowRetryPrompt(false);
    connectionAttemptRef.current = 0;
    await cleanupConnection();
    await connectToLivekit();
    if (!isChatDisabled) await silentRecover();
    setIsSending(false);
  };

  // ── Utilities ───────────────────────────────────────────────────────────────
  const getPujaUrl = (content) => {
    const m = content?.match(/https?:\/\/vedamandir\.com\/pooja[^\s]*/);
    return m ? m[0] : null;
  };
  const getDisplayContent = (content) => {
    const u = getPujaUrl(content);
    return u ? content.replace(/https?:\/\/vedamandir\.com\/pooja[^\s]*/, '').trim() : content;
  };
  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString('te-IN', { hour: '2-digit', minute: '2-digit' });

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="astro-page-root">

      {/* ── Loading overlay ──────────────────────────────────────────────── */}
      {isLoadingSession && (
        <div className="loading-overlay">
          <div className="loading-backdrop" />
          <div className="loading-spinner-container">
            <div className="spinner-ring">
              <div className="ring" />
              {/* <img src="/favicon.ico" alt="" className="spinner-logo" /> */}
            </div>
            <p className="loading-text">లోడ్ అవుతోంది...</p>
          </div>
        </div>
      )}

      {/* ── Chat (always rendered once session loads) ─────────────────────── */}
      {!isLoadingSession && (
        <div className="astrology-chatbot chat-full">
          <div className="chat-container">

            {/* Header */}
            <div className="chatbot-header">
              <div className="header-content">
                {astrologerImage && <img src={astrologerImage} alt={astrologerName} />}
                <div className="header-text">
                  <h1>{astrologerName}</h1>
                  <span>ఫ్రీ ఆస్ట్రో</span>
                </div>
                {isSending && (
                  <div className="chat-bubble">
                    <h4>Astrologer typing</h4>
                    <div className="typing">
                      <div className="dot" /><div className="dot" /><div className="dot" />
                    </div>
                  </div>
                )}
              </div>

              {(isTimerRunning || isTimerExpired) && (
                <div className="chat-timer">
                  <div className="timer-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <span className={[
                    'timer-display',
                    timerSeconds <= 60 && timerSeconds > 0 ? 'timer-warning' : '',
                    isTimerExpired ? 'timer-expired' : '',
                  ].filter(Boolean).join(' ')}>
                    {timerDisplay}
                  </span>
                  <h6>Time Left</h6>
                </div>
              )}

              <div className="free-quote" />

              {/* <div className="close-chat">
                {!isChatDisabled && !showContinueChatPrompt
                  ? <button onClick={endChatSession}>End Chat</button>
                  : <button onClick={closeChatModule}>Home</button>
                }
              </div> */}
            </div>

            {/* Retry prompt */}
            {showRetryPrompt && (
              <div className="retry-prompt">
                <p>కనెక్షన్‌లో సమస్య ఉంది</p>
                <button onClick={manualReconnect} className="retry-button">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  మళ్ళీ ప్రయత్నించండి
                </button>
              </div>
            )}

            {/* Messages list */}
            <div className="messages-wrapper">
              {messages.map((message, idx) => (
                <div key={idx} className={`message-row ${message.role}`}>
                  <div className="message-bubble">

                    <div className="message-avatar">
                      {message.role === 'system' && (
                        <img
                          className="om-icon"
                          src="/images/chat-om.svg"
                          alt="Om"
                          onError={e => {
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = '<span style="font-size:18px">🕉</span>';
                          }}
                        />
                      )}
                      {message.role === 'assistant' && astrologerImage && (
                        <img
                          src={astrologerImage}
                          alt="A"
                          style={{ maxWidth: 36, borderRadius: '1.5rem' }}
                        />
                      )}
                    </div>

                    <div className="message-content-wrapper">
                      {/* Puja image card */}
                      {getPujaUrl(message.content) && message.img_url && (
                        <div className="puja-card">
                          <a href={getPujaUrl(message.content)} target="_blank" rel="noopener noreferrer">
                            <img src={message.img_url} alt="Puja" className="puja-image" />
                          </a>
                        </div>
                      )}

                      {/* Image message (chart etc.) */}
                      {message.is_img ? (
                        <div className="chat-image-card">
                          <img
                            src={message.content}
                            alt="Chart"
                            className="chat-image"
                            onClick={() => setFullscreenImageUrl(message.content)}
                          />
                          <div className="message-time">{formatTime(message.timestamp)}</div>
                        </div>
                      ) : (
                        /* Text message */
                        <div className="message-content">
                          {message.role === 'assistant' && <h6>{astrologerName}</h6>}
                          {message.role === 'user' && <h6>You</h6>}
                          {getDisplayContent(message.content).split('\n').map((line, i, arr) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < arr.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                          <div className="message-time">{formatTime(message.timestamp)}</div>
                        </div>
                      )}

                      {/* Puja CTA button */}
                      {getPujaUrl(message.content) && message.role === 'assistant' && (
                        <a
                          href={getPujaUrl(message.content)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="book-puja-button-inline"
                        >
                          పూజలో పాల్గొనండి
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isSending && (
                <div className="message-row assistant">
                  <div className="message-bubble">
                    <div className="message-avatar">
                      {astrologerImage && (
                        <img
                          src={astrologerImage}
                          alt="A"
                          style={{ maxWidth: 36, borderRadius: '1.5rem' }}
                        />
                      )}
                    </div>
                    <div className="message-content-wrapper">
                      <div className="message-content typing-content">
                        <div className="typing-indicator">
                          <div className="typing-dot" />
                          <div className="typing-dot" />
                          <div className="typing-dot" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="input-container">
              {!isChatDisabled && !showContinueChatPrompt && (
                <form className="input-form" onSubmit={onSendMessage}>
                  <input
                    type="text"
                    className="message-input"
                    placeholder="మీ ప్రశ్న టైప్ చేయండి..."
                    value={inputMessage}
                    onChange={e => setInputMessage(e.target.value)}
                    disabled={isSending}
                  />
                  <button
                    type="submit"
                    className="send-button"
                    disabled={isSending || !inputMessage.trim()}
                  >
                    {!isSending ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    ) : (
                      <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
                        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                        <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
                        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                        <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                      </svg>
                    )}
                  </button>
                </form>
              )}

              {!isChatDisabled && showContinueChatPrompt && (
                <div className="continue-chat-prompt">
                  <button className="primary-button" onClick={continueChat}>చాట్ కొనసాగించు</button>
                </div>
              )}

              {isChatDisabled && (
                <div className="chat-disabled-message">
                  <p className="success-message">
                    ⏰ మీ సెషన్ సమయం ముగిసింది. ధన్యవాదాలు! 🙏 రేపు మళ్లీ వచ్చి మీ సందేహాన్ని అడగండి 😊
                  </p>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Fullscreen image overlay */}
      {fullscreenImageUrl && (
        <div className="fullscreen-overlay" onClick={() => setFullscreenImageUrl(null)}>
          <button className="fullscreen-close" onClick={() => setFullscreenImageUrl(null)}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <img
            src={fullscreenImageUrl}
            alt="Chart"
            className="fullscreen-image"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}